import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { LanguageService } from 'shared-lib';
import { JkuUserService } from '../jku-user.service';

/** One sidebar entry. Either navigates (`route`) or runs an `action` (e.g. logout). */
interface JkuNavItem {
  labelKey: string;
  route?: string;
  action?: () => void;
  enabled: boolean;
}

/**
 * JKU-branded shell for the parker-app (black top bar, white JKU header, bordered content
 * container with a gray sidebar). Deliberately does NOT reuse lib-app-layout /
 * lib-app-mobile-first-layout — it replicates the jku.at look.
 *
 * Applied to the *whole* app for JKU SSO users, not just the webshop: JKU asked that the site
 * never switch between the JKU and the JJames look mid-session.
 */
@Component({
  selector: 'app-jku-layout',
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, MatIconModule, TranslatePipe],
  templateUrl: './jku-layout.component.html',
  styleUrl: './jku-layout.component.scss'
})
export class JkuLayoutComponent {
  protected readonly jkuUser = inject(JkuUserService);
  protected readonly lang = inject(LanguageService);

  /**
   * Sidebar navigation. Mirrors the parker-app's own menu, rendered JKU-style — a JKU user
   * reaches every screen from here and never falls back to the JJames shell.
   */
  protected readonly sidebarItems = computed<JkuNavItem[]>(() => {
    const items: JkuNavItem[] = [
      { labelKey: 'JKU.SIDEBAR.REGISTER_PLATE', route: '/JKU-webshop', enabled: true }
    ];

    if (this.jkuUser.isAuthenticated()) {
      items.push(
        { labelKey: 'JKU.SIDEBAR.MY_PLATES', route: '/license-plates', enabled: true },
        { labelKey: 'JKU.SIDEBAR.MY_ACCOUNT', route: '/my-account', enabled: true },
        { labelKey: 'JKU.SIDEBAR.LOGOUT', action: () => this.jkuUser.logout(), enabled: true }
      );
    }

    return items;
  });

  protected switchLanguage(code: 'de' | 'en'): void {
    this.lang.use(code);
  }
}
